import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { Upload, Download, Save } from "lucide-react"
import { useState, useEffect, useCallback, useMemo, useRef } from "react"
import { getShopCustomerGroup } from "@/api/shop"
import { getSettingExcel, createSettingExcel, updateSettingExcel } from "@/api/setting"
import { uploadExcelEmployee, getUploadTask, getEmployeeList,  } from "@/api/employee"
import { ExcelUploadDialog } from "@/components/ui/excel-upload-dialog"
import { useShopStore } from "@/store/shop"
import { useAuthStore } from "@/store/auth"
import { toast } from 'sonner'
import type { CustomerGroup } from "@/types/shop"
import type { SettingExcel } from "@/types/setting"
import type { ExcelUpsertReport } from "@/types/task"
import type { Employee, EmployeeListParameters } from "@/types/employee"

type UploadType = 'upsert' | 'resign'
type BaseField = 'email' | 'phoneNumber'
type FixedGrade = 'T' | 'F'

interface SettingState {
  employeeGrade: string
  memberGrade: string
  employeeGradeFixed: FixedGrade | ''
  memberGradeFixed: FixedGrade | ''
  baseField: BaseField | ''
}

interface SettingChangeState {
  upsertChanged: boolean
  resignChanged: boolean
}

interface UploadTask {
  type: UploadType
  taskId: string
  progress: number
  status: 'uploading' | 'completed' | 'error'
  report?: ExcelUpsertReport
}

interface DialogState {
  upsertDialogOpen: boolean
  resignDialogOpen: boolean
}

interface ResultState {
  uploadResults: ExcelUpsertReport | null
  successEmployees: Employee[]
  activeTab: 'success' | 'failure'
  lastTaskId: string | null
  lastTaskType: UploadType | null
}

const INITIAL_SETTING_STATE: SettingState = {
  employeeGrade: "",
  memberGrade: "",
  employeeGradeFixed: "",
  memberGradeFixed: "",
  baseField: "",
}

const INITIAL_DIALOG_STATE: DialogState = {
  upsertDialogOpen: false,
  resignDialogOpen: false,
}

const INITIAL_RESULT_STATE: ResultState = {
  uploadResults: null,
  successEmployees: [],
  activeTab: 'success',
  lastTaskId: null,
  lastTaskType: null,
}

const INITIAL_SETTING_CHANGE_STATE: SettingChangeState = {
  upsertChanged: false,
  resignChanged: false,
}

const TEMPLATE_FILES = {
  employee_001: "회원정보등록_템플릿양식.xlsx",
  employee_002: "회원정보등록_일반계정_템플릿양식.xlsx",
  member_001: "일반회원등급일괄변경_템플릿양식.xlsx",
  member_002: "일반회원등급일괄변경_일반계정_템플릿양식.xlsx"
} as const

const POLL_INTERVAL = 1000 // 1초
const TIMEOUT_DURATION = 300000 // 5분

export function EmployeeUploadPage() {
  // State
  const [settingState, setSettingState] = useState<SettingState>(INITIAL_SETTING_STATE)
  const [customerGroups, setCustomerGroups] = useState<CustomerGroup[]>([])
  const [isLoading, setIsLoading] = useState(false)
  
  // Settings data
  const [upsertSetting, setUpsertSetting] = useState<SettingExcel | null>(null)
  const [resignSetting, setResignSetting] = useState<SettingExcel | null>(null)
  
  // Dialog states
  const [dialogState, setDialogState] = useState<DialogState>(INITIAL_DIALOG_STATE)
  
  // Upload task state
  const [uploadingTask, setUploadingTask] = useState<UploadTask | null>(null)
  
  // Result states
  const [resultState, setResultState] = useState<ResultState>(INITIAL_RESULT_STATE)
  
  // Setting change tracking
  const [settingChangeState, setSettingChangeState] = useState<SettingChangeState>(INITIAL_SETTING_CHANGE_STATE)

  // Store hooks
  const { selectedShop } = useShopStore()
  const { user } = useAuthStore()

  // 초기화 상태 추적 (StrictMode 이중 호출 방지)
  const initializedShopNoRef = useRef<number | null>(null)
  
  // Computed values
  const isCafe24User = user?.platform === 'cafe24'
  const isSuperAdmin = user?.isSuperAdmin === true
  const allowedEmployeeTypeId = user?.allowedEmployeeTypeId ?? null
  const allowedEmployeeGroupNo = user?.allowedEmployeeGroupNo ?? null
  const allowedEmployeeIsFixedGroup = user?.allowedEmployeeIsFixedGroup
  const allowedEmployeeBaseField = user?.allowedEmployeeBaseField ?? ""
  const allowedResignGroupNo = user?.allowedResignGroupNo ?? null
  const allowedResignIsFixedGroup = user?.allowedResignIsFixedGroup
  const shopNo = isCafe24User && selectedShop ? selectedShop.shopNo : 1

  // Utility functions
  const updateSettingState = useCallback((updates: Partial<SettingState>, type?: UploadType) => {
    setSettingState(prev => ({ ...prev, ...updates }))
    
    // Track setting changes
    if (type) {
      setSettingChangeState(prev => ({
        ...prev,
        [type === 'upsert' ? 'upsertChanged' : 'resignChanged']: true
      }))
    }
  }, [])

  const updateDialogState = useCallback((updates: Partial<DialogState>) => {
    setDialogState(prev => ({ ...prev, ...updates }))
  }, [])

  const updateResultState = useCallback((updates: Partial<ResultState>) => {
    setResultState(prev => ({ ...prev, ...updates }))
  }, [])

  const updateSettingChangeState = useCallback((updates: Partial<SettingChangeState>) => {
    setSettingChangeState(prev => ({ ...prev, ...updates }))
  }, [])

  // Data initialization
  const initializeData = useCallback(async (targetShopNo: number) => {
    setIsLoading(true)

    try {
      const [customerGroupResponse, upsertResponse, resignResponse] = await Promise.all([
        getShopCustomerGroup({ shopNo: targetShopNo }),
        getSettingExcel({ shopNo: targetShopNo, type: "upsert" }),
        getSettingExcel({ shopNo: targetShopNo, type: "resign" })
      ])

      const nextState: Partial<SettingState> = {}

      // Customer groups
      if (customerGroupResponse.code === 200 && customerGroupResponse.data) {
        setCustomerGroups(customerGroupResponse.data)
      } else {
        toast.error("고객 등급 정보를 가져오는데 실패했습니다.")
      }

      // Upsert settings
      if (upsertResponse.code === 200 && upsertResponse.data) {
        const data = upsertResponse.data
        setUpsertSetting(data)
        nextState.employeeGrade = data.groupNo.toString()
        nextState.baseField = data.baseField
        nextState.employeeGradeFixed = data.isFixedGroup ? "T" : "F"
      }

      // Resign settings
      if (resignResponse.code === 200 && resignResponse.data) {
        const data = resignResponse.data
        setResignSetting(data)
        nextState.memberGrade = data.groupNo.toString()
        nextState.memberGradeFixed = data.isFixedGroup ? "T" : "F"
      }

      if (!isSuperAdmin) {
        if (allowedEmployeeGroupNo !== null) {
          nextState.employeeGrade = allowedEmployeeGroupNo.toString()
        }
        if (typeof allowedEmployeeIsFixedGroup === "boolean") {
          nextState.employeeGradeFixed = allowedEmployeeIsFixedGroup ? "T" : "F"
        }
        if (allowedEmployeeBaseField) {
          nextState.baseField = allowedEmployeeBaseField
        }
        if (allowedResignGroupNo !== null) {
          nextState.memberGrade = allowedResignGroupNo.toString()
        }
        if (typeof allowedResignIsFixedGroup === "boolean") {
          nextState.memberGradeFixed = allowedResignIsFixedGroup ? "T" : "F"
        }
      }

      if (Object.keys(nextState).length > 0) {
        setSettingState(prev => ({ ...prev, ...nextState }))
      }
      setSettingChangeState(INITIAL_SETTING_CHANGE_STATE)

    } catch (error) {
      console.error("데이터 초기화 오류:", error)
      toast.error("데이터를 가져오는 중 오류가 발생했습니다.")
    } finally {
      setIsLoading(false)
    }
  }, [
    allowedEmployeeBaseField,
    allowedEmployeeGroupNo,
    allowedEmployeeIsFixedGroup,
    allowedResignGroupNo,
    allowedResignIsFixedGroup,
    isSuperAdmin,
  ])

  // Settings save handlers
  const handleSaveSettings = useCallback(async (type: UploadType) => {
    if (!isSuperAdmin) {
      toast.error("설정을 변경할 권한이 없습니다.")
      return
    }

    const { employeeGrade, memberGrade, employeeGradeFixed, memberGradeFixed, baseField } = settingState
    
    if (type === 'upsert') {
      if (!employeeGrade || !employeeGradeFixed || !baseField) {
        toast.error("모든 설정 값을 선택해주세요.")
        return
      }
    } else {
      if (!baseField) {
        toast.error("임직원 회원 정보 등록 설정을 먼저 저장해 주세요.")
        return
      }
      if (!memberGrade || !memberGradeFixed) {
        toast.error("모든 설정 값을 선택해주세요.")
        return
      }
    }

    const settingData = {
      shopNo,
      type,
      baseField: baseField as BaseField,
      groupNo: parseInt(type === 'upsert' ? employeeGrade : memberGrade),
      isFixedGroup: (type === 'upsert' ? employeeGradeFixed : memberGradeFixed) === "T"
    }

    try {
      const existingSetting = type === 'upsert' ? upsertSetting : resignSetting
      const response = existingSetting
        ? await updateSettingExcel(settingData)
        : await createSettingExcel(settingData)

      if (response.code === 200 || response.code === 201) {
        if (type === 'upsert') {
          setUpsertSetting(response.data)
        } else {
          setResignSetting(response.data)
        }
        
        const message = type === 'upsert' 
          ? "임직원 등급 설정이 저장되었습니다."
          : "일반 회원 등급 변경 설정이 저장되었습니다."
        toast.success(message)
        
        // Reset change tracking after successful save
        setSettingChangeState(prev => ({
          ...prev,
          [type === 'upsert' ? 'upsertChanged' : 'resignChanged']: false
        }))
      } else {
        toast.error(response.message || "설정 저장에 실패했습니다.")
      }
    } catch (error) {
      console.error("설정 저장 오류:", error)
      toast.error("설정 저장 중 오류가 발생했습니다.")
    }
  }, [isSuperAdmin, settingState, shopNo, upsertSetting, resignSetting])

  // Upload task monitoring
  const monitorUploadTask = useCallback(async (taskId: string, type: UploadType) => {
    let pollCount = 0
    const maxPolls = TIMEOUT_DURATION / POLL_INTERVAL

    const pollInterval = setInterval(async () => {
      pollCount++
      
      try {
        const response = await getUploadTask(taskId)
        
        if (response.data) {
          const taskData = response.data
          
          setUploadingTask({
            type,
            taskId,
            progress: taskData.rate,
            status: taskData.status === 'completed' ? 'completed' : 'uploading',
            report: taskData.report
          })

          // Upload completed
          if (taskData.rate >= 100 && taskData.status === 'completed') {
            clearInterval(pollInterval)
            if (taskData.report) {
              updateResultState({ 
                uploadResults: taskData.report,
                lastTaskId: taskId,
                lastTaskType: type,
                activeTab: taskData.report.successCount > 0 ? 'success' : 'failure'
              })
              
              if (taskData.report.successCount > 0) {
                await fetchSuccessEmployees(taskId, type)
              }
            }
          }
          // Upload failed
          else if (taskData.status === 'failed') {
            clearInterval(pollInterval)
            setUploadingTask(prev => prev ? { ...prev, status: 'error' } : null)
            toast.error("엑셀 업로드 중 오류가 발생했습니다.")
          }
        }
      } catch (error) {
        console.error("업로드 상태 조회 오류:", error)
      }

      // Timeout
      if (pollCount >= maxPolls) {
        clearInterval(pollInterval)
        if (uploadingTask?.status === 'uploading') {
          setUploadingTask(null)
          toast.error("업로드 상태 확인 시간이 초과되었습니다.")
        }
      }
    }, POLL_INTERVAL)
  }, [uploadingTask, updateResultState])

  // Upload handlers
  const handleUpload = useCallback(async (file: File, type: UploadType) => {
    const { employeeGrade, memberGrade, employeeGradeFixed, memberGradeFixed, baseField } = settingState
    const grade = type === 'upsert' ? employeeGrade : memberGrade
    const fixed = type === 'upsert' ? employeeGradeFixed : memberGradeFixed
    
    if (!grade) {
      const message = type === 'upsert' ? "변경할 임직원 등급 관련 설정을 해주세요." : "변경할 일반 회원 등급 관련 설정을 해주세요."
      toast.error(message)
      return
    }
    
    try {
      const uploadData = {
        file,
        shopNo,
        type,
        baseField: baseField as BaseField,
        groupNo: parseInt(grade),
        isFixedGroup: fixed as FixedGrade
      }

      const response = await uploadExcelEmployee(uploadData)
        
      if (response.code === 200 || response.code === 201) {
        const taskId = response.data.taskId.toString()
        
        setUploadingTask({
          type,
          taskId,
          progress: 0,
          status: 'uploading'
        })

        await monitorUploadTask(taskId, type)
      } else {
        toast.error(response.message || "엑셀 업로드에 실패했습니다.")
      }
    } catch (error) {
      console.error("엑셀 업로드 오류:", error)
      toast.error("엑셀 업로드 중 오류가 발생했습니다.")
    }
  }, [settingState, shopNo, monitorUploadTask])

  // Success employees fetch
  const fetchSuccessEmployees = useCallback(async (taskId: string, type: UploadType) => {
    try {
      const params : EmployeeListParameters = {
        shopNo,
        page: 1,
        limit: 5000,
        ...(type === 'upsert' 
          ? { lastUpsertTaskId: parseInt(taskId) } 
          : { lastResignTaskId: parseInt(taskId) }
        )
      }

      // 슈퍼 어드민이 아니면 해당 구분만 조회한다
      if (!isSuperAdmin && allowedEmployeeTypeId) {
        params.employeeTypeId = allowedEmployeeTypeId
      }
      
      const response = await getEmployeeList(params)
      
      if (response.code === 200 && response.data) {
        updateResultState({ successEmployees: response.data.employees })
      }
    } catch (error) {
      console.error('성공 리스트 로드 오류:', error)
    }
  }, [shopNo, updateResultState])

  // Template download
  const handleTemplateDownload = useCallback((type: keyof typeof TEMPLATE_FILES) => {
    const link = document.createElement('a')
    link.href = `/assets/excel/${type}.xlsx`
    link.download = TEMPLATE_FILES[type]
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }, [])

  // Dialog handlers
  const handleOpenDialog = useCallback((type: UploadType) => {
    const isChanged = type === 'upsert' ? settingChangeState.upsertChanged : settingChangeState.resignChanged
    const existingSetting = type === 'upsert' ? upsertSetting : resignSetting
    
    if (isChanged) {
      const message = type === 'upsert' 
        ? "임직원 회원 정보 등록 설정을 저장하세요."
        : "일반 회원 등급 일괄 변경 설정을 저장하세요."
      toast.error(message)
      return
    }
    
    // 저장된 데이터가 없을 때도 업로드 차단
    if (!existingSetting) {
      const message = type === 'upsert' 
        ? "임직원 회원 정보 등록 설정을 저장하세요."
        : "일반 회원 등급 일괄 변경 설정을 저장하세요."
      toast.error(message)
      return
    }
    
    setUploadingTask(null)
    setDialogState(prev => ({ ...prev, [`${type}DialogOpen`]: true }))
  }, [settingChangeState, upsertSetting, resignSetting])

  const handleViewResults = useCallback(async () => {
    if (uploadingTask?.report) {
      updateResultState({ 
        uploadResults: uploadingTask.report,
        activeTab: uploadingTask.report.successCount > 0 ? 'success' : 'failure'
      })
      
      if (uploadingTask.report.successCount > 0 && resultState.lastTaskId && resultState.lastTaskType) {
        await fetchSuccessEmployees(resultState.lastTaskId, resultState.lastTaskType)
      }
    }
  }, [uploadingTask, resultState.lastTaskId, resultState.lastTaskType, updateResultState, fetchSuccessEmployees])

  // Effects - shopNo 변경 시에만 데이터 초기화
  useEffect(() => {
    // 같은 shopNo로 이미 초기화된 경우 스킵 (StrictMode 이중 호출 방지)
    if (initializedShopNoRef.current === shopNo) return

    initializedShopNoRef.current = shopNo
    initializeData(shopNo)
  }, [shopNo]) // initializeData 제거 - 무한 루프 방지

  // Render helpers
  const renderUploadCard = useCallback((
    type: UploadType,
    title: string,
    description: string,
    templateType: keyof typeof TEMPLATE_FILES,
    gradeKey: keyof SettingState,
    fixedKey: keyof SettingState
  ) => {
    const isUpsert = type === 'upsert'
    const isChanged = type === 'upsert' ? settingChangeState.upsertChanged : settingChangeState.resignChanged
    const existingSetting = type === 'upsert' ? upsertSetting : resignSetting
    const isUploadDisabled = isChanged || !existingSetting
    
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            {title}
          </CardTitle>
          <CardDescription className="text-xs mt-2 whitespace-pre-line">
            {description}
          </CardDescription>
          <div className="pt-2">
            <Button variant="outline" onClick={() => handleTemplateDownload(templateType)}>
              <Download className="h-4 w-4" />
              엑셀 템플릿 다운로드
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-start gap-3 mb-4">
            <Select 
              value={settingState[gradeKey]} 
              onValueChange={(value) => updateSettingState({ [gradeKey]: value }, type)}
              disabled={isLoading || !isSuperAdmin}
            >
              <SelectTrigger className="w-52">
                <SelectValue placeholder={
                  isLoading 
                    ? "로딩 중..." 
                    : isUpsert ? "변경할 임직원 등급" : "변경할 일반 회원 등급"
                } />
              </SelectTrigger>
              <SelectContent>
                {customerGroups.map((group) => (
                  <SelectItem key={group.groupNo} value={group.groupNo.toString()}>
                    {group.groupName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select 
              value={settingState[fixedKey]} 
              onValueChange={(value) => updateSettingState({ [fixedKey]: value as FixedGrade }, type)}
              disabled={isLoading || !isSuperAdmin}
            >
              <SelectTrigger className="w-44">
                <SelectValue placeholder="회원 등급 고정 여부" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="T">회원 등급 고정함</SelectItem>
                <SelectItem value="F">회원 등급 고정안함</SelectItem>
              </SelectContent>
            </Select>
            
            {isUpsert && (
              <Select 
                value={settingState.baseField} 
                onValueChange={(value) => updateSettingState({ baseField: value as BaseField }, type)}
                disabled={isLoading || !isSuperAdmin}
              >
                <SelectTrigger className="w-44">
                  <SelectValue placeholder="회원 정보 매칭" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="phoneNumber">휴대폰 정보 매칭</SelectItem>
                  <SelectItem value="email">이메일 정보 매칭</SelectItem>
                </SelectContent>
              </Select>
            )}
            
            <Button onClick={() => handleSaveSettings(type)} disabled={isLoading || !isSuperAdmin}>
              <Save className="h-4 w-4" />
              저장
            </Button>
          </div>
          
          <Button 
            className={`w-full rounded-lg border border-dashed p-6 h-auto ${
              isUploadDisabled 
                ? 'bg-muted text-muted-foreground cursor-not-allowed' 
                : 'bg-transparent text-foreground hover:bg-accent'
            }`}
            variant="outline"
            onClick={() => handleOpenDialog(type)}
            disabled={isUploadDisabled}
          >
            <div className="text-center">
              <Upload className={`mx-auto h-12 w-12 ${isUploadDisabled ? 'text-muted-foreground/50' : 'text-muted-foreground'}`} />
              <div className="mt-4">
                <h3 className="text-sm font-medium">
                  {isUploadDisabled ? '설정 저장 후 업로드 가능' : '파일 업로드하기'}
                </h3>
                <p className={`text-xs mt-1 ${isUploadDisabled ? 'text-muted-foreground/70' : 'text-muted-foreground'}`}>
                  {isUploadDisabled 
                    ? (type === 'upsert' 
                        ? '임직원 회원 정보 등록 설정을 저장하세요.' 
                        : '일반 회원 등급 일괄 변경 설정을 저장하세요.'
                      )
                    : 'Excel (.xlsx, .xls) 파일만 지원됩니다'
                  }
                </p>
              </div>
            </div>
          </Button>
          
          <div className="text-xs text-muted-foreground">
            <ul className="mt-1 list-disc list-inside space-y-1">
              {isUpsert ? (
                <>
                  <li>업로드한 엑셀 임직원 정보(이메일 or 휴대폰번호)를 기준으로 쇼핑몰 가입 회원을 체크하여 임직원 회원 등급으로 일괄 변경합니다.</li>
                  <li>쇼핑몰 신규 회원 가입시 등록된 임직원 정보(이메일 or 휴대폰번호)를 체크하여 임직원 회원 등급으로 자동 변경됩니다.</li>
                </>
              ) : (
                <li>업로드한 엑셀 임직원 정보(사번)를 기준으로 임직원 목록에서 삭제되며, 일반 회원 등급으로 일괄 변경합니다.</li>
              )}
            </ul>
          </div>
        </CardContent>
      </Card>
    )
  }, [
    customerGroups,
    handleOpenDialog,
    handleSaveSettings,
    handleTemplateDownload,
    isLoading,
    isSuperAdmin,
    resignSetting,
    settingChangeState,
    settingState,
    updateSettingState,
    upsertSetting,
  ])

  const renderSuccessTable = useMemo(() => {
    if (resultState.lastTaskType === 'resign') {
      return (
        <div className="border rounded-lg p-8 text-center">
          <p className="text-lg font-medium text-green-600">
            퇴사 및 제휴 종료 처리 {resultState.uploadResults?.successCount}명 완료.
          </p>
        </div>
      )
    }

    return (
      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-center">번호</TableHead>
              <TableHead className="text-center">계정 구분명</TableHead>
              <TableHead className="text-center">사번</TableHead>
              <TableHead className="text-center">이름</TableHead>
              <TableHead className="text-center">휴대폰번호</TableHead>
              <TableHead className="text-center">이메일</TableHead>
              <TableHead className="text-center">등록일</TableHead>
              <TableHead className="text-center">수정일</TableHead>
              <TableHead className="text-center">등급 연동</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {resultState.successEmployees.map((employee, index) => (
              <TableRow key={employee.id}>
                <TableCell className="text-center">{index + 1}</TableCell>
                <TableCell className="text-center">
                  <Badge variant={employee.employeeTypeId === 1 ? "warning" : "success"}>
                    {employee.employeeTypeName?.trim()}
                  </Badge>
                </TableCell>
                <TableCell className="text-center">{employee.employeeId}</TableCell>
                <TableCell className="text-center">{employee.name}</TableCell>
                <TableCell className="text-center">{employee.phoneNumber}</TableCell>
                <TableCell className="text-center">{employee.email}</TableCell>
                <TableCell className="text-center">
                  {new Date(employee.createdAt).toLocaleDateString('ko-KR')}
                </TableCell>
                <TableCell className="text-center">
                  {new Date(employee.updatedAt).toLocaleDateString('ko-KR')}
                </TableCell>
                <TableCell className="text-center">
                  <Badge variant={employee.isLinked ? "default" : "outline"}>
                    {employee.isLinked ? "Y" : "N"}
                  </Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    )
  }, [resultState.lastTaskType, resultState.uploadResults, resultState.successEmployees])

  const renderFailureTable = useMemo(() => {
    if (!resultState.uploadResults?.failureReports?.length) return null

    return (
      <div className="border rounded-lg overflow-hidden">
        <div 
          className="bg-muted p-2 grid gap-2 text-sm font-medium text-center" 
          style={{gridTemplateColumns: '80px 120px 120px 100px 200px 200px 1fr'}}
        >
          <div>엑셀 행 번호</div>
          <div>계정 구분 번호</div>
          <div>사번</div>
          <div>이름</div>
          <div>휴대폰번호</div>
          <div>이메일</div>
          <div>실패사유</div>
        </div>
        {resultState.uploadResults.failureReports.map((failure, index) => (
          <div 
            key={index} 
            className="p-2 border-t grid gap-2 text-sm" 
            style={{gridTemplateColumns: '80px 130px 120px 100px 200px 200px 1fr'}}
          >
            <div className="text-center">{failure.rowNumber}</div>
            <div className="text-center">{failure.employeeCustomTypeId}</div>
            <div className="text-center">{failure.employeeId || "-"}</div>
            <div className="text-center">{failure.name || "-"}</div>
            <div className="text-center">{failure.phoneNumber || "-"}</div>
            <div className="text-center">{failure.email || "-"}</div>
            <div className="text-center text-red-600">{failure.reason || "-"}</div>
          </div>
        ))}
      </div>
    )
  }, [resultState.uploadResults])

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h2 className="text-3xl font-bold tracking-tight">임직원 엑셀 업로드</h2>
        <p className="text-muted-foreground text-sm mt-3">
          임직원 정보를 엑셀로 일괄 업로드하면 쇼핑몰 회원 ID를 매칭하여 설정한 회원등급으로 연동 처리합니다.
        </p>
      </div>

      {/* Upload Cards */}
      <div className="grid gap-6 md:grid-cols-2">
        {renderUploadCard(
          'upsert',
          '임직원 회원 정보 등록 / 임직원 회원 등급 일괄 변경',
          '엑셀 양식을 다운로드 받으신 후, 해당 정보를 기입하여 업로드합니다.\n엑셀 업로드시 저장한 임직원 회원 등급 및 매칭 항목(이메일, 휴대폰번호) 설정에 따라 연동처리되오니 참고해 주세요.',
          isSuperAdmin ? 'employee_001' : 'employee_002',
          'employeeGrade',
          'employeeGradeFixed'
        )}

        {renderUploadCard(
          'resign',
          '일반 회원 등급 일괄 변경 (퇴사 및 제휴 종료등)',
          '엑셀 양식을 다운로드 받으신 후, 해당 정보를 기입하여 업로드합니다.\n엑셀 업로드시 저장한 임직원 회원 등급 설정에 따라 연동처리되오니 참고해 주세요.',
          isSuperAdmin ? 'member_001' : 'member_002',
          'memberGrade',
          'memberGradeFixed'
        )}
      </div>

      {/* Upload Results */}
      <Card>
        <CardHeader>
          <CardTitle>업로드 결과</CardTitle>
          <CardDescription className="text-xs">
            파일 업로드 후 실패된 엑셀 항목을 확인할 수 있습니다.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {resultState.uploadResults ? (
            <div className="space-y-4">
              {/* Results Summary and Tabs */}
              <div className="flex items-center justify-between mb-4">
                <div className="text-sm text-muted-foreground">
                  총 등록 <span className="font-semibold text-blue-600">{resultState.uploadResults.totalCount}</span>명
                </div>
                <div className="flex gap-1">
                  <Button 
                    variant={resultState.activeTab === 'success' ? 'default' : 'outline'} 
                    size="sm"
                    onClick={() => updateResultState({ activeTab: 'success' })}
                    disabled={resultState.uploadResults.successCount === 0}
                    className="px-3 py-1 h-7 text-xs"
                  >
                    성공 {resultState.uploadResults.successCount}건
                  </Button>
                  <Button 
                    variant={resultState.activeTab === 'failure' ? 'default' : 'outline'} 
                    size="sm"
                    onClick={() => updateResultState({ activeTab: 'failure' })}
                    disabled={resultState.uploadResults.failCount === 0}
                    className="px-3 py-1 h-7 text-xs"
                  >
                    실패 {resultState.uploadResults.failCount}건
                  </Button>
                </div>
              </div>
              
              {/* Success/Failure Details */}
              {resultState.activeTab === 'success' && resultState.uploadResults.successCount > 0 && (
                <div>
                  <h4 className="font-medium mb-2">성공 내역 ({resultState.uploadResults.successCount}건)</h4>
                  {renderSuccessTable}
                </div>
              )}
              
              {resultState.activeTab === 'failure' && resultState.uploadResults.failureReports?.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">실패 내역 ({resultState.uploadResults.failureReports.length}건)</h4>
                  {renderFailureTable}
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-lg border border-dashed p-8 text-center text-muted-foreground text-sm">
              <p>업로드 내역이 없습니다.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upload Dialogs */}
      <ExcelUploadDialog
        open={dialogState.upsertDialogOpen}
        onOpenChange={(open) => updateDialogState({ upsertDialogOpen: open })}
        title="임직원 회원 정보 등록"
        description="임직원 정보가 포함된 Excel 파일을 업로드하세요."
        onUpload={(file) => handleUpload(file, 'upsert')}
        isUploading={uploadingTask?.type === 'upsert' && uploadingTask.status === 'uploading'}
        progress={uploadingTask?.type === 'upsert' ? uploadingTask.progress : 0}
        uploadStatus={
          uploadingTask?.type === 'upsert'
            ? uploadingTask.status === 'uploading' ? 'uploading'
              : uploadingTask.status === 'completed' ? 'completed'
              : 'error'
            : 'idle'
        }
        taskReport={uploadingTask?.type === 'upsert' ? uploadingTask.report || null : null}
        onViewResults={handleViewResults}
      />

      <ExcelUploadDialog
        open={dialogState.resignDialogOpen}
        onOpenChange={(open) => updateDialogState({ resignDialogOpen: open })}
        title="일반 회원 등급 일괄 변경"
        description="퇴사 또는 제휴 종료 등으로 등급을 변경할 임직원 정보가 포함된 Excel 파일을 업로드하세요."
        onUpload={(file) => handleUpload(file, 'resign')}
        isUploading={uploadingTask?.type === 'resign' && uploadingTask.status === 'uploading'}
        progress={uploadingTask?.type === 'resign' ? uploadingTask.progress : 0}
        uploadStatus={
          uploadingTask?.type === 'resign'
            ? uploadingTask.status === 'uploading' ? 'uploading'
              : uploadingTask.status === 'completed' ? 'completed'
              : 'error'
            : 'idle'
        }
        taskReport={uploadingTask?.type === 'resign' ? uploadingTask.report || null : null}
        onViewResults={handleViewResults}
      />
    </div>
  )
}
